import { createPortal } from 'react-dom'
import { ChevronRight } from 'lucide-react'
import { useI18n } from '../../i18n'
import { DoctorCard } from '../doctors/DoctorSearchKit'
import { OrellaSpinner } from '../OrellaLoader' // s123: the app's one spinner

// ShareDoctorPanel — P11 step 4 (D1). The share-code link's doctor panel (v242.207.0),
// extracted VERBATIM from DiagnoseScreen so the share entry screen's Orella card (TEST 2.19)
// shows the SAME panel, not a copy that drifts.
//
// ★★ PRESENTATIONAL. It loads nothing and writes nothing: the owner loads the rows with
//   utils/shareDoctorPanel.js#loadShareDoctorPanel (read-only) and decides what a pick does.
//   Alex, 2026-09-23: opening it has NO server-side effect and closing it leaves a share
//   code alive. scripts/check-share-link-doctor-panel.mjs holds that.
// ★ "More options" renders only when the owner passes onMoreOptions. DiagnoseScreen does (it
//   opens the manage sheet: Regenerate and Withdraw); ShareChoiceScreen has no code to manage
//   and does not.
export default function ShareDoctorPanel({ loading, yourDoctors = [], orellaDoctors = [], insurance = {}, onPick, onClose, closeLabel, onMoreOptions }) {
  const { t } = useI18n()
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end" style={{ background: 'rgba(15,30,45,0.45)' }} onClick={onClose}>
      <div className="w-full bg-white rounded-t-[22px] px-4 pt-2" onClick={(e) => e.stopPropagation()} data-safe-bottom="1" style={{ animation: 'manageUp .25s ease', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))', maxHeight: '88dvh', overflowY: 'auto', overscrollBehavior: 'contain' }}>
        <style>{`@keyframes manageUp{from{transform:translateY(40px);opacity:.4}to{transform:translateY(0);opacity:1}}`}</style>
        <div className="w-10 h-1 rounded-full mx-auto my-2" style={{ background: '#D6DEE6' }} />
        <p className="text-[17px] font-bold px-0.5 mb-1" style={{ color: '#15354F' }}>{t('diagnose.sendToOrellaDoc')}</p>
        <p className="text-[12.5px] px-0.5 mb-4 leading-relaxed" style={{ color: '#5A6B7B' }}>{t('diagnose.pickBoardCertified')}</p>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <OrellaSpinner size={24} />
          </div>
        ) : (
          <>
            {/* Hidden when empty — Alex, 2026-09-23. */}
            {yourDoctors.length > 0 && (
              <div className="mb-4">
                <div className="text-[10px] font-bold text-gray-400 tracking-wider uppercase mb-2 px-0.5">{t('diagnose.yourDoctors')}</div>
                <div className="space-y-2.5">
                  {yourDoctors.map((doctor) => (
                    <DoctorCard key={doctor.id} doc={doctor} isMyDoctor={doctor._isSeen} isFavorite={doctor._isFavorite} insurance={insurance} onTap={() => onPick(doctor)} />
                  ))}
                </div>
              </div>
            )}

            <div className="mb-3">
              <div className="text-[10px] font-bold text-gray-400 tracking-wider uppercase mb-2 px-0.5">{t('diagnose.orellaDoctors')}</div>
              {orellaDoctors.length > 0 ? (
                <div className="space-y-2.5">
                  {orellaDoctors.map((doctor) => (
                    <DoctorCard key={doctor.id} doc={doctor} isMyDoctor={false} insurance={insurance} onTap={() => onPick(doctor)} />
                  ))}
                </div>
              ) : (
                <p className="text-[12.5px] px-0.5 py-2" style={{ color: '#5A6B7B' }}>{t('doctors.myDoctorsEmpty')}</p>
              )}
            </div>
          </>
        )}

        {/* More options — Regenerate and Withdraw live in the existing manage sheet. */}
        {onMoreOptions && (
          <button onClick={onMoreOptions}
            className="flex items-center gap-3 w-full text-left rounded-[15px] p-3.5 mt-1 active:scale-[0.99] transition-transform"
            style={{ border: '1.5px solid #E2E8F0', background: '#fff' }}>
            <span className="text-[13.5px] font-semibold" style={{ color: '#15354F' }}>{t('diagnose.manageTitle')}</span>
            <ChevronRight size={18} className="ml-auto flex-shrink-0" style={{ color: '#94A3B8' }} />
          </button>
        )}

        <button onClick={onClose} className="w-full text-center py-3 mt-1 text-[13.5px] font-semibold" style={{ color: '#5A6B7B' }}>
          {closeLabel}
        </button>
      </div>
    </div>,
    document.body
  )
}
